import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '@crm-src/lib/store'
import {
  fetchCampaigns,
  fetchCampaignStats,
  fetchCampaignById,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  bulkDeleteCampaigns,
  scheduleCampaign,
  sendCampaignNow,
  pauseCampaign,
  resumeCampaign,
  duplicateCampaign,
  clearError,
  clearCache,
  optimisticUpdateCampaign,
  type EmailCampaign
} from '@crm-src/lib/store/campaignsSlice'
import type { EmailLayoutType } from '@crm-src/lib/email-layouts'

export const useEmailCampaigns = () => {
  const dispatch = useAppDispatch()
  const campaignsState = useAppSelector((state) => state.campaigns)

  // Fetch campaigns with optional filters
  const getCampaigns = useCallback(async (params?: {
    status?: string
    recipient_type?: string
    owner_id?: number
    search?: string
    page?: number
    per_page?: number
    include_stats?: boolean
  }) => {
    try {
      const result = await dispatch(fetchCampaigns(params || {})).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch campaigns:', error)
      throw error
    }
  }, [dispatch])

  // Fetch campaign statistics
  const getCampaignStats = useCallback(async () => {
    try {
      const result = await dispatch(fetchCampaignStats()).unwrap()
      return result
    } catch (error) {
      console.error('Failed to fetch campaign stats:', error)
      throw error
    }
  }, [dispatch])

  // Fetch single campaign by ID from API
  const fetchCampaign = useCallback(async (id: string) => {
    try {
      const result = await dispatch(fetchCampaignById(id)).unwrap()
      return result.campaign
    } catch (error) {
      console.error('Failed to fetch campaign:', error)
      throw error
    }
  }, [dispatch])

  // Create new campaign
  const createNewCampaign = useCallback(async (campaignData: {
    name?: string
    subject: string
    content: string
    layout?: EmailLayoutType
    target_selection?: any
    recipient_type?: 'prospects' | 'customers' | 'all'
    status?: 'not_sent' | 'scheduled'
    scheduled_at?: string
    from_address?: string
    from_name?: string
    reply_to?: string
  }) => {
    try {
      const result = await dispatch(createCampaign(campaignData)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to create campaign:', error)
      throw error
    }
  }, [dispatch])

  // Update existing campaign
  const updateExistingCampaign = useCallback(async (id: string, data: Partial<EmailCampaign>) => {
    try {
      const result = await dispatch(updateCampaign({ id, data })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to update campaign:', error)
      throw error
    }
  }, [dispatch])

  // Delete campaign (soft delete)
  const removeCampaign = useCallback(async (id: string) => {
    try {
      await dispatch(deleteCampaign(id)).unwrap()
      return true
    } catch (error) {
      console.error('Failed to delete campaign:', error)
      throw error
    }
  }, [dispatch])

  // Bulk delete campaigns (soft delete)
  const bulkRemoveCampaigns = useCallback(async (campaignIds: string[]) => {
    try {
      const result = await dispatch(bulkDeleteCampaigns(campaignIds)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to bulk delete campaigns:', error)
      throw error
    }
  }, [dispatch])

  // Schedule campaign for later sending
  const scheduleCampaignForSending = useCallback(async (id: string, scheduled_at?: string) => {
    try {
      const result = await dispatch(scheduleCampaign({ id, scheduled_at })).unwrap()
      return result
    } catch (error) {
      console.error('Failed to schedule campaign:', error)
      throw error
    }
  }, [dispatch])

  // Send campaign immediately
  const sendCampaignImmediately = useCallback(async (id: string) => {
    try {
      const result = await dispatch(sendCampaignNow(id)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to send campaign:', error)
      throw error
    }
  }, [dispatch])

  // Pause a scheduled campaign
  const pauseScheduledCampaign = useCallback(async (id: string) => {
    try {
      const result = await dispatch(pauseCampaign(id)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to pause campaign:', error)
      throw error
    }
  }, [dispatch])

  // Resume a paused campaign
  const resumePausedCampaign = useCallback(async (id: string) => {
    try {
      const result = await dispatch(resumeCampaign(id)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to resume campaign:', error)
      throw error
    }
  }, [dispatch])

  // Duplicate an existing campaign
  const duplicateExistingCampaign = useCallback(async (id: string) => {
    try {
      const result = await dispatch(duplicateCampaign(id)).unwrap()
      return result
    } catch (error) {
      console.error('Failed to duplicate campaign:', error)
      throw error
    }
  }, [dispatch])

  // Optimistic update for immediate UI feedback
  const updateCampaignOptimistically = useCallback((id: string, updates: Partial<EmailCampaign>) => {
    dispatch(optimisticUpdateCampaign({ id, updates }))
  }, [dispatch])

  // Clear error state
  const clearCampaignError = useCallback(() => {
    dispatch(clearError())
  }, [dispatch])

  // Clear cache and reset state
  const clearCampaignCache = useCallback(() => {
    dispatch(clearCache())
  }, [dispatch])

  // Send test email
  const sendTestEmail = useCallback(async (params: {
    email: string
    recipient_name?: string
    subject: string
    content: string
    layout?: EmailLayoutType
    campaign_id?: string
    attachment_blob_ids?: string[]
    // Copy the kept SAVED attachments from this campaign onto the temp test
    // campaign so the test send includes them.
    source_campaign_id?: string
    keep_attachment_ids?: string[]
    from_address?: string
    from_name?: string
    reply_to?: string
  }) => {
    try {
      const { default: api } = await import('@crm-src/lib/api')
      const response = await api.post('/campaigns/test_email', params)
      return response.data
    } catch (error: any) {
      console.error('Failed to send test email:', error)
      throw error
    }
  }, [])

  return {
    // State
    campaigns: campaignsState.campaigns || [],
    stats: campaignsState.stats,
    isLoading: campaignsState.isLoading,
    error: campaignsState.error,
    pagination: campaignsState.pagination,
    lastFetched: campaignsState.lastFetched,

    // Actions
    getCampaigns,
    getCampaignStats,
    fetchCampaign,
    createCampaign: createNewCampaign,
    updateCampaign: updateExistingCampaign,
    deleteCampaign: removeCampaign,
    bulkDeleteCampaigns: bulkRemoveCampaigns,
    scheduleCampaign: scheduleCampaignForSending,
    sendCampaign: sendCampaignImmediately,
    pauseCampaign: pauseScheduledCampaign,
    resumeCampaign: resumePausedCampaign,
    duplicateCampaign: duplicateExistingCampaign,
    updateCampaignOptimistically,
    sendTestEmail,

    // Utilities
    clearError: clearCampaignError,
    clearCache: clearCampaignCache,
  }
}

export default useEmailCampaigns



